import { Cabin, Departure, WidgetTranslations } from '../data.ts'
import { MouseEvent, useCallback, useMemo, useState } from 'react'
import { cn } from '../lib/cn.ts'
import { Badge } from './badge.tsx'
import { parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from '../icons/chevrons.tsx'
import { ShipIcon } from '../icons/ship.tsx'
import { HarbourIcon } from '../icons/harbour.tsx'
import { InformationIcon } from '../icons/information.tsx'
import { ShipInformation } from './ship-information.tsx'
import { CabinList } from './cabin-list.tsx'
import { CabinSelection } from '../types/cabin-selection.ts'
import { randomId } from '../lib/random.ts'
import { DEFAULT_LANGUAGE } from '../i18n.ts'
import { PopoverTooltip } from './popover.tsx'
import { applyDiscount, calculateCabinPrice, cheapestCabinPrice } from '@/lib/departures.ts'

export function FullyBookedDepartureListItem ({ start, end, className }: {
  departure: Departure
  start: Date
  end: Date
  className?: string
}): JSX.Element {
  const { t } = useTranslation('widget')
  return (
    <div
      className={cn('fully-booked relative p-4 @desktop:col-span-(--cols) flex justify-between items-center border border-brand-primary-light bg-brand-primary-lightest rounded-xl opacity-85', className)}
    >
      <div
        className='flex gap-4 text-brand-primary font-bold text-xs @desktop:text-base items-center flex-wrap'
      >
        <span>{t('format.date', { value: start })}</span>
        <span>{t('format.date', { value: end })}</span>
      </div>
      <div>
        <Badge variant='error'>{t('departure_statuses.fully_booked')}</Badge>
      </div>
      <div className='absolute rounded-xl isolate inset-0 bg-true-gray mix-blend-saturation' />
    </div>
  )
}

function DiscountBadge ({ discount }: { discount: Exclude<Departure['discount'], null> }): JSX.Element {
  const { t } = useTranslation('widget')
  return (
    <div>
      <Badge className='rounded-md border-warning bg-warning text-control-xs'>
        {discount.type === 'percentage' ? t('discount_percentage', { value: discount.percentage }) : t('discount_amount', { value: discount.amount })}
      </Badge>
    </div>
  )
}

export function DepartureListItem ({ departure, translations, className }: {
  departure: Departure
  translations: WidgetTranslations
  className?: string
}): JSX.Element {
  const cheapestPrice = useMemo(() => cheapestCabinPrice(departure), [departure])
  const discountedPrice = useMemo(() => applyDiscount(departure, cheapestPrice), [departure, cheapestPrice])
  const start = useMemo(() => parseISO(departure.start_date), [departure.start_date])
  const end = useMemo(() => parseISO(departure.end_date), [departure.end_date])
  const { i18n, t } = useTranslation('widget')
  const [open, setOpen] = useState(false)
  const [cabinSelection, setCabinSelection] = useState<CabinSelection>([])
  const toggle = useCallback((e: MouseEvent) => {
    const target = e.target
    if (target instanceof Element && target.closest('[data-slot="popover-trigger"]') != null) {
      return
    }
    setOpen(open => !open)
  }, [])

  const portOfDepartureName = useMemo(() => {
    const harbour = translations.harbours.find(harbour => harbour.id === departure.port_of_departure.id)
    const translation = harbour?.translations.find(translation => translation.language === i18n.language) ?? harbour?.translations.find(translation => translation.language === DEFAULT_LANGUAGE)
    return translation?.name ?? harbour?.name ?? departure.port_of_departure.name
  }, [departure, i18n.language, translations.harbours])

  const shipTranslation = useMemo(() => {
    const ship = translations.ships?.find(ship => ship.id === departure.ship.id)
    return ship?.translations.find(translation => translation.language === i18n.language) ?? ship?.translations.find(translation => translation.language === DEFAULT_LANGUAGE)
  }, [departure, i18n.language, translations.ships])

  const sgrTranslation = useMemo(() => {
    return translations.sgr.find(translation => translation.language === i18n.language) ?? translations.sgr.find(translation => translation.language === DEFAULT_LANGUAGE)
  }, [i18n.language, translations.sgr])

  const cabinMap = useMemo(() => {
    return Object.fromEntries(departure.cabins.map(cabin => [cabin.cabin_type_id, cabin]))
  }, [departure])

  const numberOfPersons = useMemo(() => cabinSelection.reduce((total, cabin) => total + cabin.occupancy, 0), [cabinSelection])

  const totalCabinPrice = useMemo(() => {
    return cabinSelection.map(({ occupancy, cabinTypeId }) => {
      const cabin = cabinMap[cabinTypeId]
      const sgr = departure.sgr * occupancy
      const cabinPrice = applyDiscount(departure, calculateCabinPrice(cabin, occupancy)) * occupancy
      return cabinPrice + sgr
    }).reduce((total, price) => total + price, 0)
  }, [cabinSelection, cabinMap, departure])

  const addCabin = useCallback((cabin: Cabin) => {
    setCabinSelection(oldCabins => [
      ...oldCabins,
      {
        id: randomId(),
        cabinTypeId: cabin.cabin_type_id,
        occupancy: Math.min(cabin.maximum_occupancy, Math.max(cabin.minimum_occupancy, 2))
      }
    ])
  }, [])

  const removeCabin = useCallback((cabinId: string) => {
    setCabinSelection(oldCabins => oldCabins.filter(cabin => cabin.id !== cabinId && cabin.cabinTypeId !== cabinId))
  }, [])

  const setCabinAmount = useCallback((cabinId: string, amount: number) => {
    setCabinSelection(oldCabins => oldCabins.map(cabin => cabin.id !== cabinId
      ? cabin
      : {
          ...cabin,
          occupancy: amount
        }))
  }, [])

  const bookingUrl = useMemo(() => {
    const map = cabinSelection.reduce((cabinMap, cabin) => {
      cabinMap.set(cabin.cabinTypeId, [...(cabinMap.get(cabin.cabinTypeId) ?? []), cabin.occupancy])
      return cabinMap
    }, new Map<string, number[]>())
    const cabinParams = Array.from(map.entries()).reduce(
      (searchParams, [typeId, occupancy]) => {
        searchParams.set(typeId, occupancy.join(','))
        return searchParams
      }, new URLSearchParams()
    )
    const params = new URLSearchParams()
    params.set('cabins', cabinParams.toString())
    params.set('locale', i18n.language)
    const url = new URL(`/departures/${departure.id}/passengers?${params.toString()}`, import.meta.env.VITE_BOOKING_URL ?? 'https://booking.boatbiketours.com')
    return url.toString()
  }, [departure, cabinSelection, i18n.language])

  if (departure.status === 'fully_booked') {
    return <FullyBookedDepartureListItem departure={departure} start={start} end={end} className={className} />
  }

  return (
    <div
      className={cn('@desktop:col-span-(--cols) border border-brand-primary-light bg-brand-primary-lightest rounded-xl grid grid-cols-subgrid divide-y divide-brand-primary-light',
        {
          '@desktop:divide-y-0': !open,
          'mb-4': open
        }, className)}
    >
      <div
        className='p-4 @desktop:grid-cols-subgrid @desktop:col-span-(--cols) grid @max-desktop:gap-2 cursor-pointer'
        onClick={toggle}
      >
        <div
          className='flex justify-between pb-2 border-b border-b-brand-primary-light @desktop:contents'
        >
          <div className='flex flex-col gap-2 items-start'>
            <div>{t('format.weekday', { ns: 'widget', value: start })}</div>
            <div className='text-brand-primary font-bold'>{t('format.date', { ns: 'widget', value: start })}</div>
          </div>
          <div className='flex flex-col gap-2 items-start'>
            <div>{t('format.weekday', { ns: 'widget', value: end })}</div>
            <div className='text-brand-primary font-bold'>{t('format.date', { ns: 'widget', value: end })}</div>
          </div>
        </div>
        <div className='flex justify-between @desktop:flex-col text-body-xs @desktop:text-base'>
          <div className='flex gap-1 items-start'>
            <div className='flex items-center h-lh'>
              <HarbourIcon className='text-brand-primary' />
            </div>
            {portOfDepartureName}
          </div>
          <div>
            {departure.tour.guidance?.map(guidance => (
              <Badge key={guidance}>{t(guidance, { keyPrefix: 'guidance_types' })}</Badge>
            ))}
          </div>
        </div>
        <div className='hidden @desktop:block' />
        <div className='flex justify-between @desktop:flex-col text-sm @desktop:text-base'>
          <div className='flex gap-1 items-start'>
            <div className='flex items-center h-lh'>
              <ShipIcon className='text-brand-primary' />
            </div>
            {shipTranslation?.name ?? departure.ship.name}
          </div>
          <div>
            {departure.status === 'on_request'
              ? (
                <PopoverTooltip
                  title={t('tooltips.on_request.title')}
                  description={t('tooltips.on_request.description')}
                >
                  <Badge variant='info'>{t(departure.status, {
                    keyPrefix: 'departure_statuses'
                  })}
                  </Badge>
                </PopoverTooltip>
                )
              : (
                <Badge variant='success'>{t(departure.status, {
                  keyPrefix: 'departure_statuses'
                })}
                </Badge>
                )}
          </div>
        </div>
        <div className='hidden @desktop:block' />
        <div className='flex justify-between @desktop:gap-6 items-center'>
          <div>
            <PopoverTooltip title={t('tooltips.guaranteed.title')} description={t('tooltips.guaranteed.description')}>
              <Badge>
                {t('widget:guaranteed')}
                <InformationIcon className='text-brand-primary' />
              </Badge>
            </PopoverTooltip>
          </div>
          {departure.discount != null && (<DiscountBadge discount={departure.discount} />)}
          <div className='@desktop:flex gap-2'>
            <div className='text-right'>
              <div className='text-body-xs'>{t('prices_from')}</div>
              {departure.discount != null &&
                <div
                  className='line-through text-heading-sm'
                >{t('widget:format.price_per_person', { value: cheapestPrice })}
                </div>}
              <div
                className='font-bold @desktop:text-xl'
              >{t('widget:format.price_per_person', { value: discountedPrice })}
              </div>
            </div>
            <button
              className='relative hidden @desktop:flex items-center cursor-pointer' type='button'
              title={open ? t('widget:button_title.close_departure', 'Collapse departure block') : t('widget:button_title.open_departure', 'Expand departure block')}
            >
              <ChevronDown className={cn('size-4 text-brand-primary transition-transform duration-200', {
                'rotate-180': open
              })}
              />
              <div className='absolute top-1/2 left-1/2 -m-3 size-6' />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <>
          <div className='@desktop:col-span-(--cols) p-4'>
            <ShipInformation departure={departure} translation={shipTranslation} />
          </div>
          <div className={cn('@desktop:col-span-(--cols) p-4 bg-white', {
            '@desktop:rounded-b-xl': cabinSelection.length === 0
          })}
          >
            <CabinList
              departure={departure} cabinSelection={cabinSelection} addCabin={addCabin}
              removeCabin={removeCabin} setCabinAmount={setCabinAmount}
              cabinTypeTranslations={translations.cabin_types}
            />
          </div>
          {cabinSelection.length > 0 && (
            <div className='bg-brand-primary-lightest p-6 @desktop:col-span-(--cols) @desktop:rounded-b-xl'>
              <div className='grid @desktop:grid-cols-2 gap-4'>
                <div className='grid @desktop:grid-cols-2'>
                  <div className='flex gap-2 items-center'>
                    <strong className='text-brand-primary text-control-md'>{t('widget:date', 'Date')}</strong>
                    <span className='text-body-sm'>{t('widget:format.date', { value: start })}</span>
                  </div>
                  <div className='flex gap-2 items-center'>
                    <strong className='text-brand-primary text-control-md'>{t('widget:ship', 'Ship')}</strong>
                    <span className='text-body-sm'>{shipTranslation?.name ?? departure.ship.name}</span>
                  </div>
                  <div className='flex gap-2 items-center'>
                    <strong className='text-brand-primary text-control-md'>{t('widget:persons', 'Persons')}</strong>
                    <span className='text-body-sm'>
                      {t('widget:persons_in_cabins', {
                        persons: t('widget:x_persons', { count: numberOfPersons }),
                        cabins: t('widget:x_cabins', { count: cabinSelection.length })
                      })}
                    </span>
                  </div>
                  <div className='flex gap-2 items-center'>
                    <strong
                      className='text-brand-primary text-control-md'
                    >{t('widget:port_of_departure', 'Port of departure')}
                    </strong>
                    <span className='text-body-sm'>{portOfDepartureName}</span>
                  </div>
                </div>
                <div className='flex flex-col @desktop:flex-row gap-6 @desktop:justify-end'>
                  <div>
                    <div className='text-body-xs flex gap-2'>
                      {t('widget:sgr', { count: numberOfPersons, value: departure.sgr })}
                      {sgrTranslation != null && (
                        <PopoverTooltip title={sgrTranslation.name} description={sgrTranslation.description ?? ''}>
                          <InformationIcon className='text-brand-primary' />
                        </PopoverTooltip>
                      )}
                    </div>
                    <div className='flex gap-2 font-bold'>
                      <div className='text-control-lg'>{t('widget:total_price', 'Total price')}</div>
                      <div className='text-heading-sm'>{t('widget:format.price', { value: totalCabinPrice })}</div>
                    </div>
                  </div>
                  <div>
                    <a
                      href={bookingUrl}
                      className='shadow-[0_-2px_0_0_#00000026_inset] flex @desktop:inline-flex items-center justify-center gap-3 bg-brand-secondary py-3 px-4 text-sm font-bold rounded-md'
                    >
                      {t('widget:start_booking', 'Start booking')}
                      <ChevronRight className='size-4' />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className='@desktop:hidden p-4 flex justify-center'>
        <button
          className='flex-1 flex justify-center cursor-pointer' type='button'
          onClick={() => setOpen(isOpen => !isOpen)}
          title={open ? t('widget:button_title.close_departure', 'Collapse departure block') : t('widget:button_title.open_departure', 'Expand departure block')}
        >
          <ChevronDown className={cn('text-brand-primary transition-transform duration-200', { 'rotate-180': open })} />
        </button>
      </div>
    </div>
  )
}
